import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { CheckCircle, PaperPlaneTilt, X, CalendarBlank, Paperclip, Trash } from "@phosphor-icons/react";

/**
 * Iter208.68 — Pannello "Azioni per il Pro" nella pagina PracticeDetail.
 *
 * Mostra i tasti custom configurati dall'admin per lo step corrente.
 * Al click apre un modal con i blocchi form; al submit:
 *  1. chiama `POST /practices/{pid}/custom-actions/{aid}/submit`,
 *  2. mostra il `confirmation_text` configurato dall'admin,
 *  3. lo storico pratica viene aggiornato lato backend.
 */
export default function PracticeCustomActionsPanel({ practiceId, onSubmitted }) {
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeAction, setActiveAction] = useState(null);

  const load = React.useCallback(() => {
    if (!practiceId) return;
    setLoading(true);
    api.get(`/practices/${practiceId}/custom-actions`)
      .then((r) => setActions(r.data?.actions || []))
      .catch(() => setActions([]))
      .finally(() => setLoading(false));
  }, [practiceId]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) return null;
  if (!actions.length) return null;

  const openAction = async (a) => {
    setActiveAction(a);
    // Iter208.88 — Marca come viste tutte le azioni nuove correntemente
    // applicabili così i pallini rossi scompaiono.
    const newIds = actions.filter((x) => x.is_new).map((x) => x.id);
    if (newIds.length > 0) {
      try {
        await api.post(`/practices/${practiceId}/custom-actions/seen`, { action_ids: newIds });
        load();
      } catch { /* silenzioso */ }
    }
  };

  return (
    <div className="mt-4" data-testid="practice-custom-actions">
      <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-[0.15em] mb-2">
        Azioni disponibili
      </div>
      <div className="flex flex-wrap gap-2">
        {actions.map((a) => (
          <Button
            key={a.id}
            variant="outline"
            className="relative border-purple-300 text-purple-700 hover:bg-purple-50 hover:border-purple-500"
            onClick={() => openAction(a)}
            data-testid={`practice-custom-action-btn-${a.id}`}
          >
            <PaperPlaneTilt size={14} weight="bold" className="mr-1.5" />
            {a.name || "Azione"}
            {a.is_new && (
              <span
                className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-red-500 rounded-full ring-2 ring-white animate-pulse"
                data-testid={`practice-custom-action-new-${a.id}`}
                aria-label="Nuova azione disponibile"
              />
            )}
          </Button>
        ))}
      </div>

      {activeAction && (
        <CustomActionModal
          action={activeAction}
          practiceId={practiceId}
          onClose={() => setActiveAction(null)}
          onDone={(res) => {
            setActiveAction(null);
            onSubmitted?.(res);
          }}
        />
      )}
    </div>
  );
}

function CustomActionModal({ action, practiceId, onClose, onDone }) {
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);

  const setAns = (bid, val) => setAnswers((a) => ({ ...a, [bid]: val }));

  const submit = async () => {
    // Validazione soft lato client (i required veri vengono validati dal backend)
    for (const b of action.blocks || []) {
      if (b.required && !answers[b.id]) {
        toast.warning(`Compila "${b.label || 'campo'}" per continuare`);
        return;
      }
      // Iter208.69 — file_upload richiede almeno 1 file se required
      if (b.required && b.type === "file_upload" && (!Array.isArray(answers[b.id]) || answers[b.id].length === 0)) {
        toast.warning(`Allega almeno un file per "${b.label || 'file'}"`);
        return;
      }
    }
    setSubmitting(true);
    try {
      const r = await api.post(`/practices/${practiceId}/custom-actions/${action.id}/submit`, { answers });
      setConfirmation(r.data?.confirmation_text || "Richiesta inviata!");
      onDone?.(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore invio richiesta");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="practice-custom-action-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PaperPlaneTilt size={18} weight="bold" className="text-purple-600" />
            {action.name || "Azione"}
          </DialogTitle>
          {!confirmation && (
            <DialogDescription>
              Compila i campi qui sotto e premi <strong>Invia</strong>. La tua richiesta arriverà
              direttamente all'operatore assegnato.
            </DialogDescription>
          )}
        </DialogHeader>

        {confirmation ? (
          <div className="py-6 text-center space-y-3" data-testid="practice-custom-action-success">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-100 text-emerald-600">
              <CheckCircle size={28} weight="fill" />
            </div>
            <div className="text-slate-800 font-medium whitespace-pre-line">{confirmation}</div>
            <Button onClick={onClose} className="bg-purple-600 hover:bg-purple-700 text-white">
              Chiudi
            </Button>
          </div>
        ) : (
          <>
            <div className="space-y-3">
              {(action.blocks || []).map((b) => (
                <BlockRenderer
                  key={b.id}
                  block={b}
                  value={answers[b.id]}
                  onChange={(v) => setAns(b.id, v)}
                  practiceId={practiceId}
                  actionId={action.id}
                />
              ))}
              {(!action.blocks || action.blocks.length === 0) && (
                <div className="text-xs italic text-slate-500 text-center py-4">
                  Nessun campo da compilare — premi Invia per notificare l'operatore.
                </div>
              )}
            </div>
            <div className="flex items-center justify-end gap-2 pt-3 border-t mt-3">
              <Button variant="ghost" onClick={onClose} disabled={submitting}>
                <X size={14} weight="bold" className="mr-1" /> Annulla
              </Button>
              <Button
                onClick={submit}
                disabled={submitting}
                className="bg-purple-600 hover:bg-purple-700 text-white"
                data-testid="practice-custom-action-submit"
              >
                {submitting ? "Invio…" : (<><PaperPlaneTilt size={14} weight="bold" className="mr-1" />Invia</>)}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function BlockRenderer({ block, value, onChange, practiceId, actionId }) {
  if (block.type === "textarea") {
    return (
      <div>
        <label className="text-xs font-semibold text-slate-700 mb-1 block">
          {block.label || "Note"} {block.required && <span className="text-red-500">*</span>}
        </label>
        <Textarea
          rows={4}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          data-testid={`practice-custom-action-block-${block.id}`}
        />
      </div>
    );
  }
  if (block.type === "calendar_slots") {
    return (
      <div data-testid={`practice-custom-action-block-${block.id}`}>
        <label className="text-xs font-semibold text-slate-700 mb-1 block">
          <CalendarBlank size={12} weight="bold" className="inline mr-1 text-purple-600" />
          {block.label || "Disponibilità"} {block.required && <span className="text-red-500">*</span>}
        </label>
        {(!block.slots || block.slots.length === 0) ? (
          <div className="text-xs italic text-slate-500 border rounded p-3">Nessuno slot configurato.</div>
        ) : (
          <div className="grid grid-cols-2 gap-2">
            {block.slots.map((s, i) => {
              const active = value && value.date === s.date && value.start === s.start && value.end === s.end;
              const label = `${new Date(s.date).toLocaleDateString("it-IT", { weekday: "short", day: "numeric", month: "short" })} · ${s.start}–${s.end}`;
              return (
                <button
                  key={`${s.date}-${s.start}-${i}`}
                  type="button"
                  onClick={() => onChange({ date: s.date, start: s.start, end: s.end })}
                  className={
                    "text-sm px-3 py-2 rounded-full border transition-all " +
                    (active
                      ? "bg-purple-600 border-purple-600 text-white shadow-sm"
                      : "bg-white border-slate-200 text-slate-700 hover:border-purple-400 hover:text-purple-700")
                  }
                  data-testid={`practice-custom-action-slot-${block.id}-${i}`}
                >
                  {label}
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  }
  if (block.type === "file_upload") {
    return <FileUploadBlock block={block} value={value} onChange={onChange} practiceId={practiceId} actionId={actionId} />;
  }
  return null;
}

function FileUploadBlock({ block, value, onChange, practiceId, actionId }) {
  const files = Array.isArray(value) ? value : [];
  const [uploading, setUploading] = useState(false);
  const maxFiles = Number(block.max_files) || 5;
  const inputRef = React.useRef(null);

  const doUpload = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    if (files.length + fileList.length > maxFiles) {
      toast.warning(`Puoi allegare al massimo ${maxFiles} file`);
      return;
    }
    setUploading(true);
    const uploaded = [...files];
    for (const f of Array.from(fileList)) {
      if (f.size > 20 * 1024 * 1024) {
        toast.error(`"${f.name}" supera 20 MB e non è stato caricato`);
        continue;
      }
      const fd = new FormData();
      fd.append("file", f);
      try {
        const r = await api.post(
          `/practices/${practiceId}/custom-actions/${actionId}/upload`,
          fd,
          { headers: { "Content-Type": "multipart/form-data" } },
        );
        uploaded.push(r.data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || `Errore upload di ${f.name}`);
      }
    }
    onChange(uploaded);
    setUploading(false);
    if (inputRef.current) inputRef.current.value = "";
  };

  const removeAt = (idx) => {
    const next = [...files];
    next.splice(idx, 1);
    onChange(next);
  };

  return (
    <div data-testid={`practice-custom-action-block-${block.id}`}>
      <label className="text-xs font-semibold text-slate-700 mb-1 block">
        <Paperclip size={12} weight="bold" className="inline mr-1 text-purple-600" />
        {block.label || "Allega file"} {block.required && <span className="text-red-500">*</span>}
        <span className="text-slate-400 font-normal ml-1">(max {maxFiles} · 20 MB ciascuno)</span>
      </label>
      <div className="border-2 border-dashed border-slate-300 rounded-lg p-3 text-center bg-slate-50/50 hover:border-purple-400 transition-colors">
        <input
          ref={inputRef}
          type="file"
          multiple
          disabled={uploading || files.length >= maxFiles}
          onChange={(e) => doUpload(e.target.files)}
          className="hidden"
          id={`fu-${block.id}`}
          data-testid={`practice-custom-action-file-input-${block.id}`}
        />
        <label
          htmlFor={`fu-${block.id}`}
          className={
            "inline-flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-md border cursor-pointer transition-colors " +
            (uploading || files.length >= maxFiles
              ? "bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed"
              : "bg-white text-purple-700 border-purple-300 hover:bg-purple-50")
          }
        >
          <Paperclip size={14} weight="bold" />
          {uploading ? "Caricamento…" : files.length ? "Aggiungi altri file" : "Scegli file"}
        </label>
      </div>
      {files.length > 0 && (
        <ul className="mt-2 space-y-1" data-testid={`practice-custom-action-files-${block.id}`}>
          {files.map((f, i) => (
            <li
              key={f.key || i}
              className="flex items-center justify-between gap-2 text-xs bg-white border border-slate-200 rounded px-2 py-1"
              data-testid={`practice-custom-action-file-${block.id}-${i}`}
            >
              <div className="flex items-center gap-1.5 min-w-0 flex-1">
                <Paperclip size={12} className="text-slate-400 shrink-0" />
                <span className="truncate">{f.name}</span>
                {f.size ? (
                  <span className="text-slate-400 shrink-0">
                    ({Math.max(1, Math.round(f.size / 1024))} KB)
                  </span>
                ) : null}
              </div>
              <button
                type="button"
                onClick={() => removeAt(i)}
                className="text-red-500 hover:bg-red-50 w-6 h-6 rounded inline-flex items-center justify-center shrink-0"
                aria-label="Rimuovi allegato"
              >
                <Trash size={12} weight="bold" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
